import React from 'react';
import { useTranslation } from 'react-i18next';

const PayslipTemplate = ({ data }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';

    if (!data) return null;

    const {
        company,
        employee,
        contract,
        lines,
        pod_lines,
        additional_hours,
        total_additional_hours,
        date_from,
        date_to,
        leave_reference_date,
        frozen_marital,
        frozen_children,
        frozen_address,
        frozen_phone,
        frozen_job_title,
        frozen_category,
        frozen_convention
    } = data;

    const MARITAL_STATUS_LABELS = {
        'single': t('payslipTemplate.maritalSingle'),
        'married': t('payslipTemplate.maritalMarried'),
        'cohabitant': t('payslipTemplate.maritalCohabitant'),
        'widower': t('payslipTemplate.maritalWidower'),
        'divorced': t('payslipTemplate.maritalDivorced')
    };

    // Helpers to find line totals
    const getLineTotal = (code) => {
        const line = lines.find(l => l.code === code);
        return line ? line.total : 0;
    };

    const getLine = (code) => lines.find(l => l.code === code);

    const formatMoney = (amount) => {
        return new Intl.NumberFormat(dateLocale, {
            style: 'currency',
            currency: company.currency || 'XOF',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    // Comme le bulletin PDF : les zéros s'affichent (ancienneté, taux).
    const formatInteger = (num) => Number(num || 0).toLocaleString(dateLocale, { maximumFractionDigits: 0 });
    const formatRate = (rate) => Number(rate || 0).toLocaleString(dateLocale, { maximumFractionDigits: 2 });

    const formatNumber = (num, decimals = 0) => {
        if (!num) return '';
        return Number(num).toLocaleString(dateLocale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Derived values
    const rht1 = getLineTotal('RHT1');
    const rjt1 = getLineTotal('RJT1');
    const ancienneteAnnees = getLineTotal('AA3');
    const ancienneteMois = getLineTotal('AM2');
    const shouldDisplayAdditionalHours = Boolean(company?.display_heure_sup) && Number(total_additional_hours) > 0;

    // Filter lines by section
    const cbpLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.category_code === 'CBP' &&
        (rjt1 * l.total) / 30 > 0
    );

    const brutLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.category_code === 'BRUT' &&
        l.total !== 0
    );

    const cbpnpniLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.category_code === 'CBNPNI' &&
        l.total !== 0
    );

    const otherLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.financial_statements_display !== 'brut' &&
        l.financial_statements_display !== 'noim' &&
        l.financial_statements_display !== 'ded' &&
        l.category_code !== 'CBP' &&
        l.category_code !== 'BRUT' &&
        l.category_code !== 'CBNPNI' &&
        l.total !== 0
    );

    const noimLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.financial_statements_display === 'noim' &&
        l.total !== 0
    );

    const dedLines = lines.filter(l =>
        l.appears_on_payslip &&
        l.financial_statements_display === 'ded' &&
        l.total !== 0
    );

    const pidLines = lines.filter(l => l.category_code === 'PID');

    return (
        <div className="bg-white mx-auto text-[0.8em] font-sans p-6 rounded shadow-sm text-gray-800" style={{ maxWidth: '210mm', minHeight: '297mm' }}>

            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-200 rounded-lg p-3 -mt-4 mb-4 shadow-sm border border-gray-100 flex justify-between">
                <div className="flex gap-4 items-center pl-4">
                    {company.logo && (
                        <img
                            src={`data:image/png;base64,${company.logo}`}
                            alt="Logo"
                            className="w-16 rounded shadow-sm"
                        />
                    )}
                    <div className="text-sm">
                        <div className="font-semibold text-lg text-slate-800">{company.name}</div>
                        <div className="text-slate-600">{company.street}</div>
                        <div className="text-slate-600">{company.street2}</div>
                        <div className="text-slate-600">
                            {company.city} {company.state} {company.zip}
                        </div>
                        <div className="text-slate-600">{company.country}</div>
                    </div>
                </div>
                <div className="text-right pr-4 flex flex-col justify-center">
                    <h3 className="m-0 font-semibold text-[#0C5B6D] text-xl">{t('payslipTemplate.payslipTitle')}</h3>
                    <p className="mt-1 text-slate-600">
                        {t('payslipTemplate.period', { from: date_from, to: date_to })}
                    </p>
                </div>
            </div>

            {/* Infos Employé & Pro */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Informations Employé */}
                <div className="bg-gray-50 rounded-lg p-3 shadow-sm border border-gray-100 h-full">
                    <h5 className="m-0 pb-1 mb-2 border-b border-gray-300 text-slate-800 font-semibold text-sm">
                        {t('payslipTemplate.employeeInfo')}
                    </h5>
                    <div className="font-semibold text-slate-800 text-base mb-1">{employee.name}</div>
                    <div className="text-slate-600 text-sm">
                        {frozen_address || employee.private_street}
                        {(frozen_address || employee.private_street) && <br />}
                        {frozen_phone || employee.private_phone}
                    </div>
                    <div className="mt-2 text-sm text-slate-600 space-y-1">
                        <div>
                            <span className="font-medium">{t('payslipTemplate.maritalStatus')} </span>
                            <span className="text-slate-800">{MARITAL_STATUS_LABELS[frozen_marital] || MARITAL_STATUS_LABELS[employee.marital] || '_'}</span>
                        </div>
                        <div>
                            <span className="font-medium">{t('payslipTemplate.numberOfChildren')} </span>
                            <span className="text-slate-800">{frozen_children || employee.children || 0}</span>
                        </div>
                        {pidLines.map((line) => (
                            <div key={line.code}>
                                <span className="font-medium">{line.name} : </span>
                                <span className="text-slate-800">{line.quantity ? Number(line.quantity).toLocaleString(dateLocale) : '_'}</span>
                            </div>
                        ))}
                        <div>
                            <span className="font-medium">{t('payslipTemplate.seniority')} </span>
                            <span className="text-slate-800">{t('payslipTemplate.seniorityValue', { years: formatInteger(ancienneteAnnees), months: formatInteger(ancienneteMois) })}</span>
                        </div>
                    </div>
                </div>

                {/* Informations Professionnelles */}
                <div className="bg-gray-50 rounded-lg p-3 shadow-sm border border-gray-100">
                    <h5 className="m-0 pb-1 mb-2 border-b border-gray-300 text-slate-800 font-semibold text-sm">
                        {t('payslipTemplate.professionalInfo')}
                    </h5>
                    <div className="mb-1 text-slate-600 text-sm flex justify-between">
                        <span className="font-medium">{t('payslipTemplate.matricule')}</span>
                        <span className="text-slate-800">{employee.matricule || '_'}</span>
                    </div>
                    {company?.country_code === 'CI' && (
                        <div className="mb-1 text-slate-600 text-sm flex justify-between">
                            <span className="font-medium">{t('payslipTemplate.cnpsNumber')}</span>
                            <span className="text-slate-800">{employee.ssnid || '_'}</span>
                        </div>
                    )}
                    <div className="mb-1 text-slate-600 text-sm flex justify-between">
                        <span className="font-medium">{t('payslipTemplate.jobTitle')}</span>
                        <span className="text-slate-800">{frozen_job_title || employee.job_title || '_'}</span>
                    </div>
                    <div className="mb-1 text-slate-600 text-sm flex justify-between">
                        <span className="font-medium">{t('payslipTemplate.category')}</span>
                        <span className="text-slate-800">{frozen_category || employee.category || '_'}</span>
                    </div>
                    <div className="mb-1 text-slate-600 text-sm flex justify-between">
                        <span className="font-medium">{t('payslipTemplate.convention')}</span>
                        <span className="text-slate-800">{frozen_convention || contract.convention || '_'}</span>
                    </div>
                    <div className="text-slate-600 text-sm flex justify-between">
                        <span className="font-medium">{t('payslipTemplate.startDate')}</span>
                        <span className="text-slate-800">{contract.date_start || '_'}</span>
                    </div>
                </div>
            </div>

            {/* Main Table */}
            <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-4">
                <table className="w-full text-xs text-left border-collapse">
                    <thead>
                        <tr className="bg-[#0C5B6D] text-white text-center">
                            <th rowSpan="2" className="p-1 border border-[#2980b9] font-medium align-middle">{t('payslipTemplate.code')}</th>
                            <th rowSpan="2" className="p-1 border border-[#2980b9] font-medium align-middle w-1/5">{t('payslipTemplate.designation')}</th>
                            <th rowSpan="2" className="p-1 border border-[#2980b9] font-medium align-middle">{t('payslipTemplate.number')}</th>
                            <th rowSpan="2" className="p-1 border border-[#2980b9] font-medium align-middle">{t('payslipTemplate.base')}</th>
                            <th colSpan="3" className="p-1 border border-[#2980b9] font-medium align-middle">{t('payslipTemplate.employeeShare')}</th>
                            <th colSpan="2" className="p-1 border border-[#2980b9] font-medium align-middle">{t('payslipTemplate.employerShare')}</th>
                        </tr>
                        <tr className="bg-[#0C5B6D] text-white text-center">
                            <th className="p-1 border border-[#2980b9] font-medium">{t('payslipTemplate.rate')}</th>
                            <th className="p-1 border border-[#2980b9] font-medium">{t('payslipTemplate.gain')}</th>
                            <th className="p-1 border border-[#2980b9] font-medium">{t('payslipTemplate.deduction')}</th>
                            <th className="p-1 border border-[#2980b9] font-medium">{t('payslipTemplate.rate')}</th>
                            <th className="p-1 border border-[#2980b9] font-medium">{t('payslipTemplate.deduction')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {/* CBP */}
                        {cbpLines.map((line, idx) => (
                            <tr key={'cbp_' + idx} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                <td className="px-2 py-1 text-right text-slate-600">
                                    {employee.is_hourly ? rht1 : rjt1}
                                </td>
                                <td className="px-2 py-1 text-right text-slate-600">
                                    {employee.is_hourly
                                        ? formatNumber(line.total)
                                        : formatNumber(line.total / (rjt1 || 1))}
                                </td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600">
                                    {employee.is_hourly
                                        ? formatNumber(rht1 * line.total)
                                        : formatNumber(line.total)}
                                </td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                            </tr>
                        ))}

                        {/* BRUT */}
                        {brutLines.map((line, idx) => (
                            <tr key={'brut_' + idx} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
                                <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                <td className="px-2 py-1 text-right text-slate-600">
                                    {/* Handle RHS specific logic roughly */}
                                    {['RHS115', 'RHS140', 'RHS160', 'RHS200'].includes(line.code)
                                        ? formatNumber(getLineTotal('RN' + line.code.slice(1)))
                                        : ''}
                                </td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600">{formatNumber(line.total)}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                            </tr>
                        ))}

                        {/* CBNPNI */}
                        {cbpnpniLines.map((line, idx) => (
                            <tr key={'cbnpni_' + idx} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600">{formatNumber(line.total)}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                            </tr>
                        ))}

                        {/* TOTAL BRUT */}
                        <tr className="bg-[#e9f7fe] font-semibold border-b border-gray-100">
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1 text-center text-[#2980b9]">{t('payslipTemplate.totalGross')}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right text-[#2980b9]">{formatNumber(getLineTotal('C401'))}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                        </tr>

                        {/* BRUT IMPOSABLE */}
                        <tr className="bg-[#e1f0fa] font-semibold border-b border-gray-100">
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1 text-center text-[#2980b9]">{t('payslipTemplate.grossTaxable')}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right text-[#2980b9]">{formatNumber(getLineTotal('C400'))}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                        </tr>

                        {/* Other Lines */}
                        {otherLines.map((line, idx) => {
                            const isAlt = idx % 2 === 0;
                            return (
                                <tr key={'other_' + idx} className={`border-b border-gray-100 ${isAlt ? 'bg-white' : 'bg-gray-50'}`}>
                                    <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                    <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                    <td className="px-2 py-1 text-right text-slate-600"></td>
                                    <td className="px-2 py-1 text-right text-slate-600">
                                        {line.amount_select === 'percentage' && formatNumber(line.amount)}
                                        {line.code === 'RCAT1702' && formatNumber(getLineTotal('PCSS1000'))}
                                        {line.amount_select === 'fix' && line.quantity}
                                    </td>
                                    <td className="px-2 py-1 text-right text-slate-600">
                                        {line.amount_select === 'percentage' && line.parent_category_code !== 'CONTS' ? formatRate(line.rate) : ''}
                                    </td>
                                    <td className="px-2 py-1 text-left text-slate-600"></td>
                                    <td className="px-2 py-1 text-right text-slate-600">
                                        {line.parent_category_code !== 'CONTS' ? formatNumber(line.total) : ''}
                                    </td>
                                    <td className="px-2 py-1 text-right text-slate-600">
                                        {line.amount_select === 'percentage' && line.parent_category_code === 'CONTS' ? formatRate(line.rate) : ''}
                                        {line.code === 'RCAT1702' ? formatRate(contract.work_accident) : ''}
                                    </td>
                                    <td className="px-2 py-1 text-right text-slate-600">
                                        {line.parent_category_code === 'CONTS' ? formatNumber(line.total) : ''}
                                    </td>
                                </tr>
                            )
                        })}

                        {/* TOTAL COTISATIONS */}
                        <tr className="bg-[#eaf6fe] font-semibold border-b border-gray-100">
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1 text-center text-[#2980b9]">{t('payslipTemplate.totalContributions')}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right text-[#2980b9]">{formatNumber(getLineTotal('RTCS1600'))}</td>
                            <td className="px-2 py-1 text-right"></td>
                            <td className="px-2 py-1 text-right text-[#2980b9]">{formatNumber(getLineTotal('RTCP1725'))}</td>
                        </tr>

                        {/* NOIM */}
                        {noimLines.map((line, idx) => (
                            <tr key={'noim_' + idx} className="bg-gray-50 border-b border-gray-100">
                                <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600">{formatNumber(line.total)}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                            </tr>
                        ))}

                        {/* DED */}
                        {dedLines.map((line, idx) => (
                            <tr key={'ded_' + idx} className="bg-white border-b border-gray-100">
                                <td className="px-2 py-1 text-slate-600">{line.code}</td>
                                <td className="px-2 py-1 font-medium text-slate-800">{line.name}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600">{formatNumber(line.total)}</td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                                <td className="px-2 py-1 text-right text-slate-600"></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* NET A PAYER */}
            <div className="rounded-lg p-3 mb-4 shadow-sm border border-gray-200 bg-white flex justify-between items-center">
                <div className="font-semibold text-lg text-slate-800">{t('payslipTemplate.netPayable')}</div>
                <div className="font-bold text-2xl text-slate-900 border-2 border-slate-200 bg-slate-50 px-4 py-1 rounded">
                    {formatMoney(getLineTotal('C2000'))}
                </div>
            </div>

            {/* Cumuls */}
            <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 mb-4 bg-white">
                <table className="w-full text-xs text-center border-collapse">
                    <thead>
                        <tr className="bg-[#0C5B6D] text-white">
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.cumulative')}</th>
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.grossTaxable')}</th>
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.totalEmployeeCharges')}</th>
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.totalEmployerCharges')}</th>
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.netTaxable')}</th>
                            <th className="p-1 border border-slate-700">{t('payslipTemplate.benefitInKind')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        <tr className="bg-gray-50 border-b border-gray-100">
                            <td className="p-1 font-medium text-slate-700">{t('payslipTemplate.periodRow')}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C400'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('RTCS1600'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('RTCP1725'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('RNI3000'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('TAN110'))}</td>
                        </tr>
                        <tr className="bg-white">
                            <td className="p-1 font-medium text-slate-700">{t('payslipTemplate.yearRow')}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C5000'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C6000'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C7000'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C10000'))}</td>
                            <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C8000'))}</td>
                        </tr>
                        {/* Cumul brut depuis le dernier retour de congé (Yros/Support#69) */}
                        {getLine('C5001') && (
                            <tr className="bg-gray-50">
                                <td className="p-1 font-medium text-slate-700">
                                    {t('payslipTemplate.leaveCumulRow')}{leave_reference_date ? ` (${leave_reference_date})` : ''}
                                </td>
                                <td className="p-1 text-slate-600">{formatNumber(getLineTotal('C5001'))}</td>
                                <td className="p-1" colSpan={4}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Compteurs & Heures supp */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 bg-white">
                    <table className="w-full text-xs text-center border-collapse">
                        <thead>
                            <tr className="bg-[#27ae60] text-white">
                                <th className="p-1 border border-[#219653]">{t('payslipTemplate.counters')}</th>
                                <th className="p-1 border border-[#219653]">{t('payslipTemplate.taken')}</th>
                                <th className="p-1 border border-[#219653]">{t('payslipTemplate.remaining')}</th>
                                <th className="p-1 border border-[#219653]">{t('payslipTemplate.acquired')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-gray-50 text-center">
                                <td className="p-1 font-medium text-[#27ae60]">{t('payslipTemplate.leave')}</td>
                                <td className="p-1 text-slate-600">{getLineTotal('RCP4')}</td>
                                <td className="p-1 text-slate-600">{getLineTotal('RCR6')}</td>
                                <td className="p-1 text-slate-600">{getLineTotal('RCA5')}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {shouldDisplayAdditionalHours && (
                    <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 bg-white">
                        <table className="w-full text-xs border-collapse">
                            <thead>
                                <tr className="bg-[#5a8fa3] text-white text-center">
                                    <th className="p-1 border border-[#2980b9]">{t('payslipTemplate.overtimeHours')}</th>
                                    <th className="p-1 border border-[#2980b9]">{t('payslipTemplate.hours')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {additional_hours.map((h, idx) => (
                                    <tr key={idx} className="bg-gray-50 text-center">
                                        <td className="p-1 font-medium text-[#2980b9] text-left px-2">{h.name}</td>
                                        <td className="p-1 text-slate-600">{formatNumber(h.hours, 2)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[#e9f7fe] text-center font-semibold">
                                    <td className="p-1 text-[#0C5B6D]">{t('payslipTemplate.total')}</td>
                                    <td className="p-1 text-[#0C5B6D]">{formatNumber(total_additional_hours, 2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Note légale */}
            <div className="bg-gray-50 rounded-lg p-3 text-center text-xs text-slate-500 shadow-sm border border-gray-100">
                <p className="m-0">
                    {t('payslipTemplate.legalNotice')}
                </p>
            </div>

        </div>
    );
};

export default PayslipTemplate;
